import React from "react";
import { useNavigate } from "react-router-dom";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { Button } from "@/components/ui/button";
import { Pencil, User, Phone, Mail, FileText, ClipboardList, Ship, CalendarDays } from "lucide-react";

const fmtDate = (d) => (d ? new Date(d).toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" }) : "—");

const Field = ({ label, children, testid }) => (
  <div className="min-w-0" data-testid={testid}>
    <div className="text-[11px] font-semibold tracking-wide text-slate-400">{label}</div>
    <div className="mt-0.5 break-words text-sm text-slate-800">{children || "—"}</div>
  </div>
);

function Links({ opp, go }) {
  const links = [
    opp.linked_rfq_id && { icon: FileText, label: opp.linked_rfq_code || "RFQ", to: `/rfqs/${opp.linked_rfq_id}`, k: "rfq" },
    opp.linked_order_id && { icon: ClipboardList, label: opp.linked_order_no || "Customer PO", to: `/orders/${opp.linked_order_id}`, k: "order" },
    opp.linked_shipment_id && { icon: Ship, label: opp.linked_shipment_no || "Shipment", to: `/logistics/shipment/${opp.linked_shipment_id}`, k: "shipment" },
  ].filter(Boolean);
  if (!links.length) return <span className="text-sm text-slate-400">Not linked yet</span>;
  return (
    <div className="flex flex-wrap gap-2">
      {links.map((l) => (
        <button key={l.k} onClick={() => go(l.to)} className="inline-flex items-center gap-1 rounded-md bg-[#E6ECF5] px-2.5 py-1 text-xs font-semibold text-[#1B4383] hover:bg-[#d9e2f0]" data-testid={`opp-peek-link-${l.k}`}><l.icon className="h-3.5 w-3.5" />{l.label}</button>
      ))}
    </div>
  );
}

export const OppPeek = ({ opp, onClose, onEdit, stageLabel, stageStyle, sourceLabel, industryLabel }) => {
  const navigate = useNavigate();
  const contacts = opp?.contacts?.length ? opp.contacts : (opp && (opp.contact_name || opp.contact_phone || opp.contact_email) ? [{ name: opp.contact_name, phone: opp.contact_phone, email: opp.contact_email }] : []);
  return (
    <Sheet open={!!opp} onOpenChange={(o) => { if (!o) onClose(); }}>
      <SheetContent side="right" className="flex w-full flex-col gap-0 overflow-y-auto p-0 sm:max-w-lg" data-testid="opp-peek">
        {opp && (<>
          <SheetHeader className="border-b border-border px-6 py-5 text-left">
            <div className="flex items-center gap-2">
              <span className="font-mono text-[11px] font-semibold text-slate-400">{opp.code}</span>
              <span className={`rounded-full px-2 py-0.5 text-[11px] font-semibold ${stageStyle}`} data-testid="opp-peek-stage">{stageLabel}</span>
            </div>
            <SheetTitle className="font-heading text-xl" data-testid="opp-peek-title">{opp.title}</SheetTitle>
            <div className="text-sm font-medium text-[#1B4383]">{opp.client_name}</div>
          </SheetHeader>
          <div className="flex-1 space-y-6 px-6 py-5">
            <div>
              <div className="mb-1.5 text-[11px] font-semibold tracking-wide text-slate-400">KEY NOTES</div>
              <div className="whitespace-pre-wrap rounded-lg border border-border bg-slate-50 p-4 text-sm leading-relaxed text-slate-800" data-testid="opp-peek-notes">{opp.description || <span className="text-slate-400">No notes yet.</span>}</div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <Field label="ESTIMATED VALUE" testid="opp-peek-value"><span className="font-mono font-semibold">{Number(opp.estimated_value_tzs) > 0 ? `TZS ${Number(opp.estimated_value_tzs).toLocaleString()}` : "—"}</span></Field>
              <Field label="INDUSTRY">{industryLabel}</Field>
              <Field label="SOURCE">{sourceLabel}</Field>
              <Field label="DATE VISITED">{opp.date_visited ? fmtDate(opp.date_visited) : "—"}</Field>
              <Field label="LOGGED BY" testid="opp-peek-owner">{opp.owner_name}</Field>
              <Field label="ASSIGNED TO">{opp.assigned_to_name}</Field>
            </div>
            <div>
              <div className="mb-1.5 text-[11px] font-semibold tracking-wide text-slate-400">CONTACTS</div>
              {contacts.length ? (
                <div className="space-y-2" data-testid="opp-peek-contacts">
                  {contacts.map((c, i) => (
                    <div key={i} className="rounded-md border border-border p-3 text-sm">
                      <div className="flex items-center gap-1.5 font-medium text-slate-800"><User className="h-3.5 w-3.5 text-slate-400" />{c.name || "—"}</div>
                      {c.phone && <a href={`tel:${c.phone}`} className="mt-1 flex items-center gap-1.5 text-xs text-[#1B4383]"><Phone className="h-3 w-3" />{c.phone}</a>}
                      {c.email && <a href={`mailto:${c.email}`} className="mt-0.5 flex items-center gap-1.5 break-all text-xs text-[#1B4383]"><Mail className="h-3 w-3" />{c.email}</a>}
                    </div>
                  ))}
                </div>
              ) : <span className="text-sm text-slate-400">No contacts recorded.</span>}
            </div>
            <div>
              <div className="mb-1.5 text-[11px] font-semibold tracking-wide text-slate-400">LINKED RFQ / PO</div>
              <Links opp={opp} go={(to) => { onClose(); navigate(to); }} />
            </div>
            <div className="flex items-center gap-4 text-[11px] text-slate-400">
              <span className="flex items-center gap-1"><CalendarDays className="h-3 w-3" />Logged {fmtDate(opp.created_at)}</span>
              {opp.updated_at && <span>Updated {fmtDate(opp.updated_at)}</span>}
            </div>
          </div>
          <div className="sticky bottom-0 flex justify-end gap-2 border-t border-border bg-white px-6 py-3">
            <Button variant="outline" onClick={onClose} data-testid="opp-peek-close">Close</Button>
            <Button onClick={() => onEdit(opp)} data-testid="opp-peek-edit"><Pencil className="mr-2 h-4 w-4" />Edit</Button>
          </div>
        </>)}
      </SheetContent>
    </Sheet>
  );
};
